/**
 * StrategyCompileDialog
 *
 * Two-step compile: step 1 captures the onboarding name / version / theme
 * choice, "Preview plan" runs a dry-run compile, and step 2 reviews the
 * per-screen plan (all creates) before the real compile executes.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import {
  BaseDialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  StyledSelectDropdown,
  StyledTextField,
} from '@modules/ui-components';
import { useProjectThemes } from '@modules/themes/hooks/useProjectThemes';
import type {
  CliFunnelHealthcheckSummary,
  CliStrategyCompileRequest,
  CliStrategyCompileThemeMode,
  CliStrategyHealthReport,
} from '@modules/shared-types';
import { StrategyHealthReportView } from '@modules/background-tasks';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';
import { StrategySyncPlanView, type StrategySyncPlanData } from './StrategySyncPlanView';

type CompileThemeChoice = CliStrategyCompileThemeMode | 'project-theme';

export interface StrategyCompilePayload extends CliStrategyCompileRequest {}

export interface StrategyCompilePreviewResult {
  plan?: StrategySyncPlanData['plan'];
  warnings?: string[];
  screenCount?: number;
  blockCount?: number;
  healthcheck?: CliFunnelHealthcheckSummary;
  strategyHealthReport?: CliStrategyHealthReport;
  error?: string;
}

export interface StrategyCompileDialogProps {
  open: boolean;
  projectId: string;
  defaultName: string;
  defaultVersionName?: string;
  currentThemeAvailable: boolean;
  currentThemeSourceId?: string | null;
  loading?: boolean;
  onClose: () => void;
  /** Dry-run compile returning the per-screen plan for the review step. */
  onPreview: (payload: StrategyCompilePayload) => Promise<StrategyCompilePreviewResult>;
  /**
   * Real compile. A refusal (e.g. G3c's 409 `strategy_generating`) comes back as
   * `{ error }` so the dialog — which stays open over the canvas — shows it in
   * its own inline alert instead of a snackbar behind the modal.
   */
  onCompile: (payload: StrategyCompilePayload) => Promise<{ error?: string } | void>;
}

export const StrategyCompileDialog: React.FC<StrategyCompileDialogProps> = ({
  open,
  projectId,
  defaultName,
  defaultVersionName = 'Compiled from strategy',
  currentThemeAvailable,
  currentThemeSourceId,
  loading = false,
  onClose,
  onPreview,
  onCompile,
}) => {
  const { themes, activeTheme, loading: themesLoading, error: themesError } = useProjectThemes(projectId);

  const [step, setStep] = useState<'form' | 'plan'>('form');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<(StrategySyncPlanData & {
    healthcheck?: CliFunnelHealthcheckSummary;
    strategyHealthReport?: CliStrategyHealthReport;
  }) | null>(null);
  // The error of the step the owner is on: the dry-run preview on the form step,
  // the real compile on the plan step (both rendered as this dialog's own alert).
  const [stepError, setStepError] = useState<string | null>(null);
  const [healthExpanded, setHealthExpanded] = useState(false);

  const [name, setName] = useState(defaultName);
  const [versionName, setVersionName] = useState(defaultVersionName);
  const [themeChoice, setThemeChoice] = useState<CompileThemeChoice>(
    currentThemeAvailable ? 'strategy-current' : 'project-default',
  );
  const [projectThemeId, setProjectThemeId] = useState('');

  useEffect(() => {
    if (!open) return;

    setName(defaultName);
    setVersionName(defaultVersionName);
    setThemeChoice(currentThemeAvailable ? 'strategy-current' : 'project-default');
    setStep('form');
    setPreviewData(null);
    setStepError(null);
    setHealthExpanded(false);
  }, [open, defaultName, defaultVersionName, currentThemeAvailable]);

  useEffect(() => {
    if (!open || themes.length === 0) return;

    const preferredThemeId = activeTheme?.id || themes[0]?.id || '';
    setProjectThemeId((current) => (
      current && themes.some((theme) => theme.id === current)
        ? current
        : preferredThemeId
    ));
  }, [open, activeTheme?.id, themes]);

  useEffect(() => {
    if (!currentThemeAvailable && themeChoice === 'strategy-current') {
      setThemeChoice('project-default');
    }
  }, [currentThemeAvailable, themeChoice]);

  const projectThemeOptions = useMemo(() => themes.map((theme) => ({
    value: theme.id,
    label: `${theme.isActive ? 'Active: ' : ''}${theme.name || 'Untitled theme'} - ${theme.id}`,
  })), [themes]);

  const trimmedName = name.trim();
  const trimmedVersionName = versionName.trim();
  const busy = loading || previewLoading;
  const canSubmit = Boolean(trimmedName)
    && Boolean(trimmedVersionName)
    && !busy
    && (themeChoice !== 'project-theme' || Boolean(projectThemeId));

  const buildPayload = useCallback((): StrategyCompilePayload => (
    themeChoice === 'project-theme'
      ? { name: trimmedName, versionName: trimmedVersionName, projectThemeId }
      : { name: trimmedName, versionName: trimmedVersionName, themeMode: themeChoice }
  ), [projectThemeId, themeChoice, trimmedName, trimmedVersionName]);

  const handleClose = useCallback(() => {
    if (busy) return;
    onClose();
  }, [busy, onClose]);

  const handlePreview = useCallback(async () => {
    if (!canSubmit) return;

    setPreviewLoading(true);
    setStepError(null);
    try {
      const result = await onPreview(buildPayload());
      if (result.error) {
        setStepError(result.error);
        return;
      }
      setPreviewData({
        plan: result.plan,
        warnings: result.warnings,
        healthcheck: result.healthcheck,
        strategyHealthReport: result.strategyHealthReport,
      });
      setStep('plan');
    } catch (err) {
      setStepError((err as Error)?.message || 'Compile preview failed');
    } finally {
      setPreviewLoading(false);
    }
  }, [buildPayload, canSubmit, onPreview]);

  const handleCompile = useCallback(async () => {
    if (busy) return;
    setStepError(null);
    try {
      // G3c (owner rule 2026-09-11): a refused compile is never silent — the
      // server's text lands in the plan step's alert, the dialog stays open.
      const result = await onCompile(buildPayload());
      if (result?.error) setStepError(result.error);
    } catch (err) {
      setStepError((err as Error)?.message || 'Compile failed');
    }
  }, [busy, buildPayload, onCompile]);

  const planScreenCount = previewData?.plan?.length ?? 0;

  const primaryAction = useMemo(() => (step === 'form'
    ? {
      label: previewLoading ? 'Preparing plan...' : 'Preview plan',
      onClick: handlePreview,
      disabled: !canSubmit,
      loading: previewLoading,
      'data-testid': 'strategy-compile-submit',
    }
    : {
      label: loading ? 'Compiling...' : `Compile ${planScreenCount} screen${planScreenCount === 1 ? '' : 's'}`,
      onClick: handleCompile,
      disabled: busy || planScreenCount === 0,
      loading,
      'data-testid': 'strategy-compile-confirm',
    }), [busy, canSubmit, handleCompile, handlePreview, loading, planScreenCount, previewLoading, step]);

  const secondaryAction = useMemo(() => ({
    label: 'Cancel',
    onClick: handleClose,
    disabled: busy,
  }), [handleClose, busy]);

  const additionalActions = useMemo(() => (step === 'plan'
    ? [{
      label: 'Back',
      onClick: () => {
        // Fix round 1 (M4): the step owns its error — a compile refusal must not
        // keep hanging over the form the owner came back to edit.
        setStepError(null);
        setStep('form');
      },
      disabled: busy,
    }]
    : undefined), [busy, step]);

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      maxWidth={step === 'plan' ? 'md' : 'sm'}
      fullWidth
      mode="modal"
      data-testid="strategy-compile-dialog"
      aria-labelledby="strategy-compile-dialog-title"
    >
      <DialogHeader
        title="Compile Strategy to Onboarding"
        showCloseButton={false}
      />

      <DialogContent>
        {step === 'form' ? (
          <Stack spacing={2}>
            {themesError && (
              <Alert severity="warning">
                {themesError.message}
              </Alert>
            )}

            {stepError && (
              <Alert severity="error" data-testid="strategy-sync-error">
                {stepError}
              </Alert>
            )}

            <StyledTextField
              label="Onboarding Name"
              value={name}
              onChange={setName}
              fullWidth
              required
              disabled={busy}
              data-testid="strategy-compile-name"
            />

            <StyledTextField
              label="Version Name"
              value={versionName}
              onChange={setVersionName}
              fullWidth
              required
              disabled={busy}
              data-testid="strategy-compile-version-name"
            />

            <Stack spacing={1}>
              <Typography variant="subtitle2">
                Theme
              </Typography>

              <RadioGroup
                value={themeChoice}
                onChange={(event) => setThemeChoice(event.target.value as CompileThemeChoice)}
                data-testid="strategy-compile-theme-choice"
              >
                <FormControlLabel
                  value="strategy-current"
                  control={<Radio />}
                  disabled={busy || !currentThemeAvailable}
                  label={currentThemeSourceId ? `Use current strategy theme (${currentThemeSourceId})` : 'Use current strategy theme'}
                />
                <FormControlLabel
                  value="project-default"
                  control={<Radio />}
                  disabled={busy}
                  label={activeTheme ? `Use default project theme (${activeTheme.name})` : 'Use configured default theme'}
                />
                <FormControlLabel
                  value="project-theme"
                  control={<Radio />}
                  disabled={busy || themesLoading || themes.length === 0}
                  label="Choose a project theme"
                />
              </RadioGroup>
            </Stack>

            {themeChoice === 'project-theme' && (
              <StyledSelectDropdown
                label="Project Theme"
                value={projectThemeId}
                onChange={(value) => setProjectThemeId(String(value))}
                options={projectThemeOptions}
                required
                fullWidth
                disabled={busy || themesLoading}
                loading={themesLoading}
                loadingText="Loading project themes..."
                noOptionsText="No project themes found"
                placeholder="Select project theme"
                data-testid="strategy-compile-project-theme-id"
              />
            )}
          </Stack>
        ) : (
          <Box data-testid="strategy-compile-plan">
            {(previewData?.healthcheck || previewData?.strategyHealthReport) && (
              <Box
                sx={{ mb: 2, borderLeft: '4px solid', borderColor: (previewData.healthcheck?.errors ?? 0) > 0 ? 'error.main' : (previewData.healthcheck?.warnings ?? 0) > 0 ? 'warning.main' : 'success.main', pl: 1.5 }}
              >
                <Button
                  size="small"
                  color={(previewData.healthcheck?.errors ?? 0) > 0 ? 'error' : (previewData.healthcheck?.warnings ?? 0) > 0 ? 'warning' : 'success'}
                  onClick={() => setHealthExpanded((current) => !current)}
                  data-testid={STRATEGY_CANVAS.compileHealthSummary}
                  sx={{ p: 0, minWidth: 0, textTransform: 'none', justifyContent: 'flex-start' }}
                >
                  Healthcheck: {previewData.healthcheck?.warnings ?? 0} warnings · {previewData.healthcheck?.errors ?? 0} errors · compile is not blocked
                </Button>
                {previewData.strategyHealthReport && (
                  <Typography variant="body2" color="text.secondary" data-testid={STRATEGY_CANVAS.compileHealthStrategyLine}>
                    Strategy health: {previewData.strategyHealthReport.summary.findingsTotal} findings · {Object.entries(previewData.strategyHealthReport.summary.byCheck).slice(0, 3).map(([key, count]) => `${key} ${count}`).join(' · ') || 'clean'}
                    {previewData.strategyHealthReport.routerPathTrace
                      ? ` · router path trace: ${previewData.strategyHealthReport.routerPathTrace.distinctPaths} distinct paths`
                      : ''}
                  </Typography>
                )}
                <Collapse in={healthExpanded} unmountOnExit>
                  <Box sx={{ mt: 1 }} data-testid={STRATEGY_CANVAS.compileHealthFindings}>
                    {previewData.strategyHealthReport && (
                      <StrategyHealthReportView
                        report={previewData.strategyHealthReport}
                        variant="compact"
                        maxFindingsPerGroup={3}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      The persisted full report is available in the completed compile or generation task.
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            )}
            <StrategySyncPlanView
              data={previewData}
              direction="compile"
              error={stepError}
              resolutions={{}}
              onResolutionsChange={() => {}}
            />
          </Box>
        )}
      </DialogContent>

      <DialogFooter
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        additionalActions={additionalActions}
      />
    </BaseDialog>
  );
};

StrategyCompileDialog.displayName = 'StrategyCompileDialog';
